/* lk-lens.js v2 — Eyewear Essentials prescription flow (2026-09-12)
   Rewritten from the 2026-09-10 copy. What changed and why:
   - Lens packages are matched to the flow by FAMILY (regex on the variant's
     lens-type name), not by an exact string. The old code only knew six names,
     so "Prescription Blue-Cut", "Anti-Glare", "Night-Drive" and "Zero-Power
     Blue-Cut" never rendered: zero power was a dead end and the cheapest
     prescription lens on show was Rs.799.
   - Sunglasses get a "tinted single vision" path. If the Lenses product has no
     tinted variant yet, the customer can still add the frame and get a quote on
     WhatsApp instead of being offered a clear lens.
   - Power limits per family (section setting, JSON) grey out packages the lab
     cannot make for the entered power.
   - The "Lenses from Rs.X" figure is the cheapest package for the chosen use,
     not the cheapest variant overall.
   - The step counter updates the moment a use is chosen.
   - The prescription is remembered in this browser (and prefilled from the
     customer's saved metafield when logged in) so a second frame is one tap.
   - ?rx=edit on a product URL reopens the drawer prefilled (cart "Change" link).
   - A cart attribute summarises prescription state for staff / Flow (see
     lk-rx-attrs.js). Frame lines and lens lines share a hidden _bundle key.
   JS-off: the native form still adds the frame on its own. */
(function () {
'use strict';
var root = document.querySelector('[data-ee-lens]');
if (!root) return;

function money(cents, fmt) {
  var n = (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return (fmt || 'Rs. {{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, n).replace(/<[^>]+>/g, '');
}
function num(v) {
  if (v == null) return NaN;
  v = String(v).trim().replace(/−/g, '-').replace(/,/g, '.').replace(/^\+/, '');
  if (v === '') return NaN;
  return Number(v);
}
function fmtD(v) { return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toFixed(2); }
function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
function store(k, v) {
  try {
    if (v === undefined) return JSON.parse(localStorage.getItem(k) || 'null');
    if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v));
  } catch (e) { return null; }
}

/* ---------- lens families ---------- */
function fam(type) {
  var t = String(type || '').toLowerCase();
  if (/zero|plano|no power|0 power|non.?power/.test(t)) return 'plano';
  if (/progressive|bifocal|multifocal/.test(t)) return 'progressive';
  if (/photochrom|transition/.test(t)) return 'photochromic';
  if (/tint|sun/.test(t)) return 'sv-tinted';
  if (/night/.test(t)) return 'sv-night';
  if (/anti.?glare|anti.?reflect/.test(t)) return 'sv-ag';
  if (/blue/.test(t)) return 'sv-blue';
  if (/single|\bsv\b|clear|prescription/.test(t)) return 'sv';
  return 'other';
}
var DESC = {
  'sv': 'Clear single-vision lens. Anti-glare, scratch-resistant, UV400.',
  'sv-blue': 'Single vision with a blue-light filter for screens. Anti-glare, scratch-resistant, UV400.',
  'sv-ag': 'Single vision with an anti-reflective coat for screens and headlights.',
  'sv-night': 'Single vision with a light yellow tint that cuts headlight glare at night.',
  'photochromic': 'Clear indoors, darkens in sunlight. One pair inside and out.',
  'sv-tinted': 'Your power in a tinted sunglass lens. UV400.',
  'plano': 'No prescription. Blue-light filter for screens, or just the look.',
  'progressive': 'Distance to reading in one lens, no visible line.',
  'other': ''
};
var FLOWS = {
  'sv-distance': ['sv', 'sv-blue', 'sv-ag', 'sv-night', 'photochromic'],
  'sv-reading':  ['sv', 'sv-blue', 'sv-ag', 'sv-night', 'photochromic'],
  'sv-tinted':   ['sv-tinted'],
  'plano':       ['plano'],
  'progressive': ['progressive']
};
var USAGE_LABEL = { 'sv-distance': 'Single vision · distance', 'sv-reading': 'Single vision · reading', 'sv-tinted': 'Single vision · tinted', 'plano': 'Zero power', 'progressive': 'Progressive' };

function selector(root) {
  var cfg = JSON.parse(root.querySelector('[data-ee-lens-config]').textContent);
  var rules = {};
  try { rules = JSON.parse(root.dataset.powerRules || '{}') || {}; } catch (e) { rules = {}; }
  var variants = (cfg.variants || []).filter(function (v) { return v.available; }).map(function (v) { v.fam = fam(v.type); return v; });
  var form = document.querySelector('form[action*="/cart/add"]');
  var addBtn = form && form.querySelector('.ee-add-button');
  if (!form || !addBtn) return;
  var isSun = root.dataset.sun === 'true';
  var drawer = root.querySelector('.eel-drawer');
  var body = root.querySelector('[data-eel-body]');
  var steps = { usage: root.querySelector('[data-eel-step="usage"]'), rx: root.querySelector('[data-eel-step="rx"]'), pkg: root.querySelector('[data-eel-step="package"]') };
  var nextBtn = root.querySelector('[data-eel-next]');
  var backBtn = root.querySelector('[data-eel-back]');
  var summary = root.querySelector('[data-eel-summary]');
  var stepLabel = root.querySelector('[data-eel-step-label]');
  var errorEl = root.querySelector('[data-eel-error]');
  var pkgWrap = root.querySelector('[data-eel-packages]');
  var fileInput = root.querySelector('[data-eel-file]');
  var fileName = root.querySelector('[data-eel-filename]');
  var addRow = root.querySelector('[data-eel-add]');
  var savedBox = root.querySelector('[data-eel-saved]');
  var maxBytes = (Number(root.dataset.maxMb) || 10) * 1024 * 1024;
  var wa = (root.dataset.whatsapp || '').replace(/\D/g, '');
  var framePrice = Number(root.dataset.framePrice) || 0;
  var noteEl = document.querySelector('[data-lk-note]');

  function packagesFor(usage) {
    var allowed = FLOWS[usage] || [];
    return variants.filter(function (v) { return allowed.indexOf(v.fam) >= 0; })
      .sort(function (a, b) { return a.price - b.price || allowed.indexOf(a.fam) - allowed.indexOf(b.fam); });
  }
  function minFor(usage) { var l = packagesFor(usage); return l.length ? l[0].price : null; }

  /* PDP note under the buttons: real "from" prices per path */
  (function writeNote() {
    if (!noteEl) return;
    var rxMin = minFor(isSun ? 'sv-tinted' : 'sv-distance'), zpMin = minFor('plano'), bits = [];
    if (rxMin != null) bits.push('Prescription lenses from ' + money(rxMin, cfg.moneyFormat));
    else if (isSun) bits.push('Power sunglasses: tinted lenses quoted on WhatsApp');
    if (zpMin != null) bits.push('zero power from ' + money(zpMin, cfg.moneyFormat));
    bits.push('or the frame only');
    noteEl.textContent = bits.join(' · ') + '. Enter, upload or WhatsApp your prescription.';
  })();

  addBtn.setAttribute('aria-haspopup', 'dialog');
  var state = { usage: null, mode: 'enter', pkg: null, file: null, flow: ['usage'], at: 0 };

  form.addEventListener('submit', function (e) { e.preventDefault(); open(); });

  root.querySelectorAll('input[name="eel-usage"]').forEach(function (r) {
    r.addEventListener('change', function () {
      state.usage = r.value;
      state.pkg = null;
      state.flow = r.value === 'none' ? ['usage'] : r.value === 'plano' ? ['usage', 'pkg'] : ['usage', 'rx', 'pkg'];
      if (addRow) addRow.hidden = r.value !== 'progressive';
      nextBtn.disabled = false;
      nextBtn.textContent = r.value === 'none' ? 'Add frame to bag' : 'Continue';
      stepLabel.textContent = 'Step 1 of ' + state.flow.length;
      renderSummary();
    });
  });
  root.querySelectorAll('[data-eel-mode]').forEach(function (t) {
    t.addEventListener('click', function () {
      state.mode = t.dataset.eelMode;
      root.querySelectorAll('[data-eel-mode]').forEach(function (x) { x.setAttribute('aria-selected', x === t ? 'true' : 'false'); });
      root.querySelectorAll('[data-eel-pane]').forEach(function (p) { p.hidden = p.dataset.eelPane !== state.mode; });
      showError('');
    });
  });
  var laterCopy = root.querySelector('[data-eel-later-copy]');
  if (laterCopy && wa) laterCopy.innerHTML = 'After checkout, send a photo of your prescription on WhatsApp to <b>+' + wa + '</b> with your order number. We message you first. Lenses are cut once we have it.';

  /* ---------- upload ---------- */
  var drop = root.querySelector('[data-eel-drop]');
  function setFile(f) {
    state.file = null; fileName.hidden = true;
    if (!f) return;
    if (f.size > maxBytes) { showError('That file is ' + (f.size / 1048576).toFixed(1) + ' MB. Please keep it under ' + Math.round(maxBytes / 1048576) + ' MB.'); fileInput.value = ''; return; }
    if (!/^image\/|pdf$|heic|heif/i.test(f.type || '') && !/\.(jpe?g|png|webp|pdf|heic|heif)$/i.test(f.name)) { showError('Please upload a photo (JPG, PNG, HEIC) or a PDF.'); fileInput.value = ''; return; }
    state.file = f;
    fileName.textContent = '✓ ' + f.name + ' (' + (f.size / 1048576).toFixed(1) + ' MB)';
    fileName.hidden = false;
    showError('');
  }
  fileInput.addEventListener('change', function () { setFile(fileInput.files[0]); });
  ['dragenter', 'dragover'].forEach(function (ev) { drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-over'); }); });
  ['dragleave', 'drop'].forEach(function (ev) { drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-over'); if (ev === 'drop' && e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }); });
  steps.rx.addEventListener('input', function (e) { if (e.target.matches('input')) { e.target.removeAttribute('aria-invalid'); showError(''); } });
  root.querySelector('[name="pd_unknown"]').addEventListener('change', function (e) { root.querySelector('[name="pd"]').disabled = e.target.checked; });

  /* ---------- saved prescription ---------- */
  function savedRx() { return store('ee_rx') || cfg.customerRx || null; }
  function fillFrom(rx) {
    if (!rx) return;
    ['od', 'os'].forEach(function (e) {
      var o = rx[e] || {};
      field(e + '_sph').value = o.sph != null ? o.sph : '';
      field(e + '_cyl').value = o.cyl != null ? o.cyl : '';
      field(e + '_axis').value = o.axis != null ? o.axis : '';
    });
    if (rx.add != null && field('add')) field('add').value = rx.add;
    field('pd').value = rx.pd || '';
    field('pd_unknown').checked = !!rx.pdUnknown;
    field('pd').disabled = !!rx.pdUnknown;
    if (field('rx_name')) field('rx_name').value = rx.name || '';
    var t = root.querySelector('[data-eel-mode="enter"]'); if (t) t.click();
  }
  function renderSaved() {
    if (!savedBox) return;
    var rx = savedRx();
    if (!rx || !rx.od) { savedBox.hidden = true; return; }
    var when = rx.ts ? new Date(rx.ts) : null;
    var who = rx.name ? ' for ' + esc(rx.name) : '';
    savedBox.innerHTML = '<span>Use the prescription saved' + who + (when && !isNaN(when) ? ' on ' + when.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '') + '?</span>' +
      '<button type="button" class="eel-mini" data-eel-use-saved>Fill it in</button><button type="button" class="eel-mini eel-mini-quiet" data-eel-forget-saved>Forget</button>';
    savedBox.hidden = false;
    savedBox.querySelector('[data-eel-use-saved]').addEventListener('click', function () { fillFrom(rx); showError(''); });
    savedBox.querySelector('[data-eel-forget-saved]').addEventListener('click', function () { store('ee_rx', null); cfg.customerRx = null; renderSaved(); });
  }
  function rxObject() {
    var o = { ts: new Date().toISOString(), name: (field('rx_name') ? field('rx_name').value.trim() : ''), pd: field('pd').value.trim(), pdUnknown: field('pd_unknown').checked };
    ['od', 'os'].forEach(function (e) { o[e] = { sph: field(e + '_sph').value.trim(), cyl: field(e + '_cyl').value.trim(), axis: field(e + '_axis').value.trim() }; });
    if (field('add') && field('add').value.trim()) o.add = field('add').value.trim();
    return o;
  }
  function rememberRx() {
    if (state.mode !== 'enter') return;
    store('ee_rx', rxObject());
  }

  /* ---------- navigation ---------- */
  nextBtn.addEventListener('click', function () {
    var cur = state.flow[state.at];
    if (cur === 'rx' && !validateRx()) return;
    if (cur === 'pkg' && !state.pkg) { showError('Choose a lens package to continue.'); return; }
    if (state.at === state.flow.length - 1) { submit(); return; }
    state.at++;
    show();
  });
  backBtn.addEventListener('click', function () { if (state.at > 0) { state.at--; show(); } });
  root.querySelectorAll('[data-eel-close]').forEach(function (b) { b.addEventListener('click', close); });
  drawer.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = drawer.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]):not([type="radio"]):not([hidden]), input[type="radio"]:checked, [tabindex="0"]');
    f = Array.prototype.filter.call(f, function (x) { return x.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  var lastFocus = null;
  function open() {
    lastFocus = document.activeElement;
    root.hidden = false;
    document.body.classList.add('eel-lock');
    setTimeout(function () { root.classList.add('is-open'); drawer.focus(); }, 20);
    state.at = 0; show();
  }
  function close() {
    root.classList.remove('is-open');
    document.body.classList.remove('eel-lock');
    var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(function () { root.hidden = true; if (lastFocus && lastFocus.focus) lastFocus.focus(); }, rm ? 0 : 320);
  }
  function show() {
    var cur = state.flow[state.at];
    steps.usage.hidden = cur !== 'usage';
    steps.rx.hidden = cur !== 'rx';
    steps.pkg.hidden = cur !== 'pkg';
    backBtn.hidden = state.at === 0;
    stepLabel.textContent = 'Step ' + (state.at + 1) + ' of ' + state.flow.length;
    if (cur === 'rx') renderSaved();
    if (cur === 'pkg') renderPackages();
    nextBtn.disabled = cur === 'usage' ? !state.usage : cur === 'pkg' ? !state.pkg : false;
    nextBtn.textContent = state.at === state.flow.length - 1 ? (state.usage === 'none' ? 'Add frame to bag' : 'Add to bag') : 'Continue';
    showError('');
    body.scrollTop = 0;
    renderSummary();
  }
  function showError(msg) { errorEl.textContent = msg; errorEl.hidden = !msg; }
  function field(n) { return root.querySelector('[name="' + n + '"]'); }
  function bad(n, msg) { var f = field(n); if (f) { f.setAttribute('aria-invalid', 'true'); if (!errorEl.textContent) f.focus(); } showError(msg); return false; }
  function step25(v) { return Math.abs(v * 4 - Math.round(v * 4)) < 1e-6; }

  /* ---------- validation ---------- */
  function validateRx() {
    if (state.mode === 'later') return true;
    if (state.mode === 'upload') {
      if (!state.file) return bad(null, 'Please choose a photo or PDF of your prescription, or switch to “Enter values”.');
      var pdU = field('pd_upload').value.trim();
      if (pdU && !pdOk(pdU)) return bad('pd_upload', 'PD is usually between 50 and 80 mm (or two numbers like 31/31).');
      return true;
    }
    var eyes = ['od', 'os'];
    for (var i = 0; i < eyes.length; i++) {
      var e = eyes[i];
      var sph = num(field(e + '_sph').value), cyl = field(e + '_cyl').value.trim() === '' ? 0 : num(field(e + '_cyl').value), axis = field(e + '_axis').value.trim();
      if (isNaN(sph)) return bad(e + '_sph', 'Enter SPH for both eyes (use 0.00 if there is no power).');
      if (sph < -20 || sph > 20 || !step25(sph)) return bad(e + '_sph', 'SPH goes from −20.00 to +20.00 in steps of 0.25.');
      if (isNaN(cyl) || cyl < -6 || cyl > 6 || !step25(cyl)) return bad(e + '_cyl', 'CYL goes from −6.00 to +6.00 in steps of 0.25.');
      if (cyl !== 0) {
        var ax = num(axis);
        if (isNaN(ax) || ax < 1 || ax > 180 || ax !== Math.round(ax)) return bad(e + '_axis', 'AXIS is a whole number from 1 to 180 — it is on the prescription next to CYL.');
      }
    }
    if (state.usage === 'progressive') {
      var add = num(field('add').value);
      if (isNaN(add) || add < 0.75 || add > 3.5 || !step25(add)) return bad('add', 'ADD for progressives is usually +0.75 to +3.50.');
    }
    var pdUnknown = field('pd_unknown').checked, pd = field('pd').value.trim();
    if (!pdUnknown && pd && !pdOk(pd)) return bad('pd', 'PD is usually between 50 and 80 mm (or two numbers like 31/31).');
    return true;
  }
  function pdOk(v) {
    var m = v.replace(/\s/g, '').match(/^(\d{2}(?:\.\d)?)(?:\/(\d{2}(?:\.\d)?))?$/);
    if (!m) return false;
    if (m[2]) return +m[1] >= 25 && +m[1] <= 40 && +m[2] >= 25 && +m[2] <= 40;
    return +m[1] >= 50 && +m[1] <= 80;
  }
  function powers() {
    if (state.mode !== 'enter') return null;
    var s = ['od_sph', 'os_sph'].map(function (n) { return num(field(n).value) || 0; });
    var c = ['od_cyl', 'os_cyl'].map(function (n) { return num(field(n).value) || 0; });
    return { sphMin: Math.min.apply(null, s), sphMax: Math.max.apply(null, s), cylAbs: Math.max.apply(null, c.map(Math.abs)), abs: Math.max.apply(null, s.map(Math.abs)) };
  }
  function outOfRange(v, p) {
    if (!p) return '';
    var r = rules[v.fam] || rules['*'];
    if (!r) return '';
    if (r.sph && (p.sphMin < r.sph[0] || p.sphMax > r.sph[1])) return 'Made for SPH ' + fmtD(r.sph[0]) + ' to ' + fmtD(r.sph[1]) + ' only';
    if (r.cyl && p.cylAbs > r.cyl) return 'Made for CYL up to ±' + r.cyl.toFixed(2) + ' only';
    return '';
  }
  function rxSummary() {
    if (state.usage === 'plano' || state.usage === 'none') return {};
    if (state.mode === 'later') return { 'Prescription': 'Will send on WhatsApp after checkout' };
    var nm = field('rx_name') ? field('rx_name').value.trim() : '';
    if (state.mode === 'upload') { var o = { 'Prescription': state.file }; var p = field('pd_upload').value.trim(); if (p) o['PD'] = p + ' mm'; if (nm) o['Name on prescription'] = nm; return o; }
    var out = {};
    ['od', 'os'].forEach(function (e) {
      var sph = num(field(e + '_sph').value), cyl = field(e + '_cyl').value.trim() === '' ? 0 : num(field(e + '_cyl').value), ax = field(e + '_axis').value.trim();
      var s = 'SPH ' + fmtD(sph);
      if (cyl) s += ' · CYL ' + fmtD(cyl) + ' · AXIS ' + ax;
      out[e === 'od' ? 'Right eye (OD)' : 'Left eye (OS)'] = s;
    });
    if (state.usage === 'progressive') out['ADD'] = fmtD(num(field('add').value));
    out['PD'] = field('pd_unknown').checked ? 'Not sure — please help me measure' : (field('pd').value.trim() ? field('pd').value.trim() + ' mm' : 'Not given');
    if (nm) out['Name on prescription'] = nm;
    out['Prescription'] = 'Entered on the website';
    /* hidden (underscore) copy in machine form: Shopify Flow copies it into customer.metafields.custom.rx */
    out['_rx'] = JSON.stringify(rxObject());
    return out;
  }

  /* ---------- packages ---------- */
  function recommendedIndex() { var p = powers(); var a = p ? p.abs : 0; return a > 4 ? '1.67' : a > 2 ? '1.61' : '1.56'; }
  function renderPackages() {
    var list = packagesFor(state.usage);
    var p = powers();
    var rec = recommendedIndex();
    pkgWrap.innerHTML = '';
    if (state.pkg && !state.pkg.quote && list.indexOf(state.pkg) < 0) state.pkg = null;
    var lead = root.querySelector('[data-eel-package-lead]');
    if (!list.length) {
      /* Nothing in the Lenses product for this path yet (e.g. tinted). Add the
         frame; the lens price is confirmed on WhatsApp before cutting. */
      lead.textContent = state.usage === 'sv-tinted'
        ? 'Tinted prescription lenses are priced per frame and power. Add the frame now and we confirm the lens price with you on WhatsApp before cutting.'
        : 'No lens package is listed for this option yet. Add the frame and we will quote the lenses on WhatsApp.';
      var q = el('label', 'eel-choice');
      q.innerHTML = '<input type="radio" name="eel-pkg" value="quote"><span><b>' + (state.usage === 'sv-tinted' ? 'Tinted single vision' : 'Lenses') + ' · quoted</b><small>Frame added now, lens price confirmed on WhatsApp. No payment for lenses until you agree.</small></span><span class="eel-price">Quote</span>';
      var qi = q.querySelector('input');
      qi.checked = !!(state.pkg && state.pkg.quote);
      qi.addEventListener('change', function () { state.pkg = { quote: true, type: state.usage === 'sv-tinted' ? 'Tinted single vision' : 'Lenses', index: '', price: 0 }; nextBtn.disabled = false; showError(''); renderSummary(); });
      pkgWrap.appendChild(q);
      return;
    }
    lead.textContent = (p && p.abs > 8) ? 'Your power is above ±8.00 — choose a package and we will confirm the best lens with you before cutting.' : 'Every package includes anti-glare, scratch-resistant and UV400 coatings.';
    var recVariant = null;
    for (var k = 0; k < list.length; k++) { if (list[k].index === rec && !outOfRange(list[k], p)) { recVariant = list[k]; break; } }
    if (!recVariant) { for (var k2 = 0; k2 < list.length; k2++) { if (!outOfRange(list[k2], p)) { recVariant = list[k2]; break; } } }
    list.forEach(function (v) {
      var why = outOfRange(v, p);
      var isRec = v === recVariant;
      var lab = el('label', 'eel-choice' + (why ? ' eel-choice-off' : ''));
      lab.innerHTML = '<input type="radio" name="eel-pkg" value="' + v.id + '"' + (why ? ' disabled' : '') + '><span><b>' + esc(v.type) + (v.index ? ' · ' + esc(v.index) : '') + (isRec ? '<span class="eel-badge">Recommended</span>' : '') + '</b><small>' +
        (why ? esc(why) + '. ' : '') + (DESC[v.fam] || '') + (v.index === '1.61' ? ' Thinner, lighter lens.' : v.index === '1.67' ? ' Thinnest lens for strong powers.' : '') + '</small></span><span class="eel-price">' + money(v.price, cfg.moneyFormat) + '</span>';
      var inp = lab.querySelector('input');
      inp.checked = state.pkg === v;
      inp.addEventListener('change', function () { state.pkg = v; nextBtn.disabled = false; showError(''); renderSummary(); });
      pkgWrap.appendChild(lab);
    });
  }
  function renderSummary() {
    var lines = [['Frame', money(framePrice, cfg.moneyFormat)]];
    var total = framePrice, plus = '';
    if (state.usage === 'none') lines.push(['Lenses', 'None']);
    else if (state.pkg && state.pkg.quote) { lines.push(['Lenses · ' + state.pkg.type, 'quoted on WhatsApp']); plus = '+'; }
    else if (state.pkg) { lines.push(['Lenses · ' + state.pkg.type + (state.pkg.index ? ' ' + state.pkg.index : ''), money(state.pkg.price, cfg.moneyFormat)]); total += state.pkg.price; }
    else if (state.usage) { var m = minFor(state.usage); lines.push(['Lenses', m != null ? 'from ' + money(m, cfg.moneyFormat) : 'quoted on WhatsApp']); plus = '+'; }
    summary.innerHTML = '<dl>' + lines.map(function (l) { return '<dt>' + l[0] + '</dt><dd>' + l[1] + '</dd>'; }).join('') + '<dt class="eel-total">Total</dt><dd class="eel-total">' + money(total, cfg.moneyFormat) + plus + '</dd></dl>';
  }

  /* ---------- submit ---------- */
  function submit() {
    var qty = Math.max(1, parseInt((form.querySelector('[name="quantity"]') || {}).value || '1', 10));
    var frameId = form.querySelector('[name="id"]').value;
    var frameTitle = root.dataset.frameTitle;
    var bundle = 'ee-' + Date.now().toString(36);
    nextBtn.disabled = true; nextBtn.classList.add('is-busy'); showError('');
    var lensKey = null;
    var rx = rxSummary();
    var p = Promise.resolve();
    var frameProps = { '_bundle': bundle };
    if (state.usage === 'none') {
      frameProps['Lenses'] = 'Frame only — no lenses';
    } else if (state.pkg && state.pkg.quote) {
      frameProps['Lenses'] = state.pkg.type + ' — lens price to be quoted on WhatsApp';
      frameProps['Use'] = USAGE_LABEL[state.usage] || state.usage;
      Object.keys(rx).forEach(function (k) { if (!(rx[k] instanceof File)) frameProps[k] = rx[k]; });
      if (rx['Prescription'] instanceof File) {
        /* the file has to travel as multipart; send the frame line that way */
        p = p.then(function () {
          var fd = new FormData();
          fd.append('id', frameId); fd.append('quantity', qty);
          Object.keys(frameProps).forEach(function (k) { fd.append('properties[' + k + ']', frameProps[k]); });
          fd.append('properties[Prescription]', rx['Prescription'], rx['Prescription'].name);
          return fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'Could not add the frame.'); }); });
        });
        frameProps = null;
      }
    } else {
      frameProps['Lenses'] = state.pkg.type + (state.pkg.index ? ' · ' + state.pkg.index : '');
      p = p.then(function () {
        var fd = new FormData();
        fd.append('id', state.pkg.id);
        fd.append('quantity', qty);
        fd.append('properties[Frame]', frameTitle);
        fd.append('properties[Lens type]', state.pkg.type);
        fd.append('properties[Index]', state.pkg.index);
        fd.append('properties[Use]', USAGE_LABEL[state.usage] || state.usage);
        Object.keys(rx).forEach(function (k) { if (rx[k] instanceof File) fd.append('properties[' + k + ']', rx[k], rx[k].name); else fd.append('properties[' + k + ']', rx[k]); });
        fd.append('properties[_bundle]', bundle);
        return fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'Could not add the lenses.'); lensKey = j.key; }); });
      });
    }
    if (frameProps) {
      p = p.then(function () {
        return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(frameId), quantity: qty, properties: frameProps }] }) })
          .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'Could not add the frame.'); }); });
      });
    }
    p.then(function () {
      try { rememberRx(); } catch (e) {}
      return window.LKRX && LKRX.syncAttributes ? LKRX.syncAttributes() : null;
    }).then(function () {
      window.location.href = '/cart';
    }).catch(function (err) {
      var msg = (err && err.message) || 'Something went wrong. Please try again.';
      var done = function () { nextBtn.disabled = false; nextBtn.classList.remove('is-busy'); showError(msg); };
      if (lensKey) fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: lensKey, quantity: 0 }) }).then(done, done); else done();
    });
  }

  /* ?rx=edit — reopen from the cart's "Change lenses or prescription" link */
  if (/[?&]rx=edit\b/.test(location.search)) {
    var s = savedRx();
    setTimeout(function () { open(); if (s) { try { fillFrom(s); } catch (e) {} } }, 250);
  }
}

/* boot last: the family tables above are plain vars, so they must exist before selector() runs */
try { selector(root); } catch (e) { if (window.console) console.warn('lk-lens', e); }
})();
